import React from 'react';

import { Helmet } from 'react-helmet';

import { Box } from '@mui/material';

import DebugModal from '~/components/DebugModal/DebugModal';
import PromoLogsContainer from '~/components/PromoV3/PromoLogs';

import { addDays } from '~/services/TimeService';

import { withTenant } from '../../hoc';
import PromoFailures from '../PromoFailure/PromoFailures';
import PromoUsers from '../PromoUsers';
import usePromoMeta from '../hooks/usePromoMeta';
import usePromoRequestUrl from '../hooks/usePromoRequestUrl';

import PromoRequests from './PromoRequests';

interface Props {
  tenant: App.Tenant;
}

const DEFAULT_PAGE_SIZE = 10;

function PromoRequestsContainer({ tenant }: Props) {
  const {
    urlState,
    updateUrlState,
    isDevMode,
    setIsDevMode,
    isExpanded,
    setIsExpanded,
    isItemsExpanded,
    setIsItemsExpanded,
    isLoyaltyItemsExpanded,
    setIsLoyaltyItemsExpanded,
    isVoucherLogsExpanded,
    setIsVoucherLogsExpanded,
    updateLogPagination,
    updateFailurePagination,
    updateUserPagination,
    updateLogDateFilters,
    updateIncludeDeleted,
    isLogsExpanded,
    setIsLogsExpanded,
    isFailuresExpanded,
    setIsFailuresExpanded,
    isUsersExpanded,
    setIsUsersExpanded,
  } = usePromoRequestUrl({
    brand: tenant.brand,
  });

  const { promoMeta, isPromoMetaLoading } = usePromoMeta();

  return (
    <Box>
      <Helmet>
        <title>Promo Requests</title>
      </Helmet>
      <Box mt={2}>
        <PromoRequests
          initUserId={urlState.userId}
          initCodeName={urlState.codeName}
          initPage={urlState.page ?? 0}
          initLimit={urlState.limit ?? DEFAULT_PAGE_SIZE}
          brand={tenant.brand}
          promoMeta={promoMeta}
          defaultExpanded={urlState.expanded ?? false}
          isPromoMetaLoading={isPromoMetaLoading}
          urlState={urlState}
          updateUrlState={updateUrlState}
          isDevMode={isDevMode}
          setIsDevMode={setIsDevMode}
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          isItemsExpanded={isItemsExpanded}
          setIsItemsExpanded={setIsItemsExpanded}
          isLoyaltyItemsExpanded={isLoyaltyItemsExpanded}
          setIsLoyaltyItemsExpanded={setIsLoyaltyItemsExpanded}
          isVoucherLogsExpanded={isVoucherLogsExpanded}
          setIsVoucherLogsExpanded={setIsVoucherLogsExpanded}
          isLogsExpanded={isLogsExpanded}
          setIsLogsExpanded={setIsLogsExpanded}
        />
        <PromoLogsContainer
          promo={{
            code_name: urlState.codeName ?? 'ALL',
            promo_type: 'unknown',
          }}
          userIdFilter={urlState.userId}
          meta={promoMeta}
          defaultExpanded={urlState.expanded ?? false}
          initPage={urlState.logPage ?? 0}
          initLimit={urlState.logLimit ?? DEFAULT_PAGE_SIZE}
          onPageChange={updateLogPagination}
          expanded={isLogsExpanded}
          onExpandedChange={setIsLogsExpanded}
          initLogAfter={urlState.logAfter ? new Date(urlState.logAfter) : addDays(-740, new Date())}
          initLogBefore={urlState.logBefore ? new Date(urlState.logBefore) : undefined}
          includeDeleted={urlState.includeDeleted}
          onDateFiltersChange={updateLogDateFilters}
          onIncludeDeletedChange={updateIncludeDeleted}
          updateUrlState={updateUrlState}
        />
        <PromoFailures
          initUserId={urlState.userId}
          initCodeName={urlState.codeName}
          brand={tenant.brand}
          promoMeta={promoMeta}
          initPage={urlState.failurePage ?? 0}
          initLimit={urlState.failureLimit ?? DEFAULT_PAGE_SIZE}
          defaultExpanded={urlState.expanded ?? false}
          onPageChange={updateFailurePagination}
          expanded={isFailuresExpanded}
          onExpandedChange={setIsFailuresExpanded}
        />
        <PromoUsers
          initPage={urlState.userPage ?? 0}
          initLimit={urlState.userLimit ?? DEFAULT_PAGE_SIZE}
          defaultExpanded={urlState.expanded ?? false}
          onPageChange={updateUserPagination}
          expanded={isUsersExpanded}
          onExpandedChange={setIsUsersExpanded}
        />
        <DebugModal data={promoMeta} type="generic" />
      </Box>
    </Box>
  );
}

export default withTenant(PromoRequestsContainer);
